"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Vibrant } from "node-vibrant/browser";
import SplitText from "@/components/bits/SplitText/SplitText";
import AuroraBackground from "@/components/aurora-background";
import EpisodeCards from "@/components/episode-cards";
import CategoryFollowButton from "@/components/category/category-follow-button";
import CategoryRssButton from "@/components/category/category-rss-button";
import { Session } from "@/lib/user";
import { getAuroraPaletteHexes } from "@/lib/aurora-palette";
import { notoSerif } from "@/lib/fonts";

interface CategoryPageContentProps {
  categoryId: string;
  categoryName: string;
  categoryDescription: string | null;
  categorySlug: string;
  session: Session | null;
  rssFeedUrl: string;
}

export default function CategoryPageContent({
  categoryId,
  categoryName,
  categoryDescription,
  categorySlug,
  session,
  rssFeedUrl,
}: CategoryPageContentProps) {
  const { resolvedTheme } = useTheme();
  const [auroraColors, setAuroraColors] = useState<string[] | null>(null);
  const [showAurora, setShowAurora] = useState(false);
  const [firstEpisodeCover, setFirstEpisodeCover] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!firstEpisodeCover) {
      setShowAurora(false);
      setAuroraColors(null);
      return;
    }

    setShowAurora(false);

    const extractColorsFromCover = async (cover: string) => {
      const wsrvLowResUrl = `https://wsrv.nl/?url=${encodeURIComponent(
        cover
      )}&w=96&h=96&fit=cover&we&output=webp&q=50&maxage=1y&default=${encodeURIComponent(
        "https://wsrv.nl/lichtenstein.jpg"
      )}`;

      const wsrvFallbackUrl = `https://wsrv.nl/?url=${encodeURIComponent(
        cover
      )}&w=64&h=64&fit=cover&we&output=webp&q=45&default=${encodeURIComponent(
        "https://wsrv.nl/lichtenstein.jpg"
      )}`;

      let palette: any;
      try {
        palette = await Vibrant.from(wsrvLowResUrl).getPalette();
      } catch {
        try {
          palette = await Vibrant.from(wsrvFallbackUrl).getPalette();
        } catch {
          palette = await Vibrant.from(cover).getPalette();
        }
      }

      return getAuroraPaletteHexes(
        palette,
        resolvedTheme === "light" ? "light" : "dark"
      );
    };

    const hydrateAurora = async () => {
      try {
        const colors = await extractColorsFromCover(firstEpisodeCover);
        if (!colors || cancelled) return;
        setAuroraColors(colors);
        requestAnimationFrame(() => {
          if (!cancelled) setShowAurora(true);
        });
      } catch {
        // Intentionally keep aurora hidden if extraction fails.
      }
    };

    hydrateAurora();

    return () => {
      cancelled = true;
    };
  }, [firstEpisodeCover, resolvedTheme]);

  return (
    <main className="m-4 md:mr-6 relative pb-24">
      {auroraColors ? (
        <div
          className={`transition-opacity duration-700 ease-out ${showAurora ? "opacity-100" : "opacity-0"}`}
        >
          <AuroraBackground
            colorStops={auroraColors}
            opacity={32}
           
          />
        </div>
      ) : null}

      <div className="md:mx-4 mx-2 relative z-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span
              className={`text-2xl md:text-3xl font-semibold ${notoSerif.className}`}
            >
              <SplitText text={categoryName} delay={20} />
            </span>
            {categoryDescription && (
              <p className="text-sm md:text-base text-muted-foreground mt-4">
                {categoryDescription}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CategoryFollowButton
              categoryId={categoryId}
              categoryName={categoryName}
              userId={session?.user?.id}
              size="sm"
            />
            <CategoryRssButton
              rssFeedUrl={rssFeedUrl}
              categoryName={categoryName}
            />
          </div>
        </div>
        <EpisodeCards
          category={categorySlug}
          session={session}
          onFirstEpisodeCoverChange={setFirstEpisodeCover}
        />
      </div>
    </main>
  );
}
